import { createFileRoute } from "@tanstack/react-router";
import AssetPage from "src/components/app/AssetPage/AssetPage";
import PortalPage from "src/components/app/PortalPage/PortalPage";
import Button from "src/components/common/Button/Button";
import ContentPanel from "src/components/common/ContentPanel/ContentPanel";
import Skeleton from "src/components/common/Skeleton/Skeleton";
import EmptyStatus from "src/components/status/EmptyStatus/EmptyStatus";
import LoadingStatus from "src/components/status/LoadingStatus/LoadingStatus";
import { DeferredLoaderData } from "src/lib/LoaderData";
import { raceLoaderData } from "src/lib/LoaderResult";
import { useAppRouter } from "src/lib/Navigation";
import { scopeFrom } from "src/lib/ScopedLoaders";
import { type AssetPageData, getAssetPageData } from "src/server/functions/Portal";
import { useExtracted } from "#i18n";

// An asset the visitor can't see (or that doesn't exist) is a 404.
export const Route = createFileRoute("/_authed/portal/proj_{$projectKey}/assets/$assetId")({
  loader: (context) =>
    raceLoaderData(
      scopeFrom(context).then(({ projectId }) => {
        const { params } = context;
        return getAssetPageData({ data: { projectId: projectId ?? "", assetId: params.assetId } });
      }),
    ),
  component: AssetDetailPage,
});

function AssetDetailPage() {
  const { assetId } = Route.useParams();
  return (
    // Another asset starts from its own data (or its loading state), not the last asset's.
    <DeferredLoaderData key={assetId} result={Route.useLoaderData()}>
      {(data) => (data ? <AssetDetail data={data} /> : <AssetPageLoading />)}
    </DeferredLoaderData>
  );
}

function AssetDetail({ data }: { data: AssetPageData }) {
  // Signed out: the `_authed` layout renders the sign-in page instead of this one.
  if (data.state === "signed-out") return null;
  if (data.state === "no-project") return <NoProjectSelected />;
  return (
    <AssetPage
      key={data.asset.id}
      asset={data.asset}
      initialUrls={data.assetUrls}
      initialFolders={data.folders}
      initialParentAsset={data.parentAsset}
      initialChildAssetsPage={data.childAssetsPage}
      initialEventsPage={data.eventsPage}
      initialConnectorDeliveries={data.connectorDeliveries}
      initialConnectors={data.connectors}
      initialHashLists={data.hashLists}
    />
  );
}

/** The asset page while its data is still loading (client navigations past the loader cut-off). */
function AssetPageLoading() {
  const translateMessage = useExtracted();
  return (
    <PortalPage>
      <PortalPage.Header sticky>
        <PortalPage.Heading>
          <PortalPage.Title>
            <Skeleton width={240} aria-hidden="true" />
          </PortalPage.Title>
        </PortalPage.Heading>
      </PortalPage.Header>
      <PortalPage.Content>
        <ContentPanel>
          <ContentPanel.Content>
            <LoadingStatus title={translateMessage("Loading asset...")} />
          </ContentPanel.Content>
        </ContentPanel>
      </PortalPage.Content>
    </PortalPage>
  );
}

function NoProjectSelected() {
  const translateMessage = useExtracted();
  const router = useAppRouter();
  return (
    <PortalPage>
      <PortalPage.Header>
        <PortalPage.Heading>
          <PortalPage.Title>{translateMessage("No Project Selected")}</PortalPage.Title>
          <PortalPage.Description>
            {translateMessage("Please select a project to view assets")}
          </PortalPage.Description>
        </PortalPage.Heading>
      </PortalPage.Header>
      <PortalPage.Content>
        <EmptyStatus
          action={
            <Button
              label={translateMessage("Back")}
              variant="outline"
              size="sm"
              onClick={() => router.push("/portal")}
            />
          }
        />
      </PortalPage.Content>
    </PortalPage>
  );
}
